/**
 * Meta Pixel utility — RELOAD Nutrition
 * Pixel ID: 854333003938436
 *
 * Consent-gated: pixel only loads after the user accepts marketing cookies.
 * All standard events use fbq('track', ...) — never trackCustom.
 * fbq.disablePushState = true prevents Meta's auto SPA pushState tracking.
 *
 * Implementation note: no proxy/monkey-patching of fbq. Doing so breaks
 * post-load event delivery because fbevents.js sets callMethod on window.fbq
 * while the proxy delegates to a captured stub whose callMethod stays undefined.
 */

const PIXEL_ID = '854333003938436';
const CONSENT_KEY = 'reload_cookie_consent';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const UTM_SESSION_KEY = 'reload_utms';

// ─── Consent check ──────────────────────────────────────────────────────────

function isConsentGiven() {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'accepted';
  } catch {
    return false;
  }
}

// ─── Init ────────────────────────────────────────────────────────────────────

/**
 * Loads Meta Pixel and fires PageView for the current page.
 * - Safe to call multiple times (guarded by window.__fbqLoaded).
 * - Only runs when marketing consent has been granted.
 * - Sets fbq.disablePushState = true BEFORE init so fbevents.js never
 *   attaches its own pushState listener (that listener fires events Meta
 *   labels "Custom" in Events Manager).
 * - Fires PageView internally so returning-user initial loads are captured
 *   even if the React Router effect ran before this.
 */
export function initPixel() {
  if (!isConsentGiven() || typeof window === 'undefined' || window.__fbqLoaded) return;

  // Build the fbq stub — matches the official Meta snippet exactly.
  if (!window.fbq) {
    const n = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    window.fbq = n;
  }

  // Disable Meta's built-in SPA pushState auto-tracking.
  // Must be set BEFORE fbevents.js loads so the script respects it on init.
  window.fbq.disablePushState = true;

  // Inject fbevents.js
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const first = document.getElementsByTagName('script')[0];
  first.parentNode.insertBefore(script, first);

  // Standard init + initial PageView.
  // Firing PageView here ensures the first-page event is always captured.
  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');

  window.__fbqLoaded = true;
}

// ─── Standard event trackers (all use 'track', never 'trackCustom') ─────────

/**
 * Fires PageView on React Router navigation.
 * Call on pathname changes only — NOT on initial mount
 * (initPixel already fires the initial PageView).
 */
export function trackPageView() {
  if (typeof window.fbq !== 'function') return;
  window.fbq('track', 'PageView');
}

/**
 * Fires when the /shop product page is viewed.
 * @param {number} value - product price (GBP)
 */
export function trackViewContent(value) {
  if (typeof window.fbq !== 'function') return;
  window.fbq('track', 'ViewContent', {
    content_ids: ['reload-male-performance-formula'],
    content_name: 'RELOAD Male Performance Formula',
    content_type: 'product',
    value: parseFloat(value.toFixed(2)),
    currency: 'GBP',
  });
}

/**
 * Fires when an item is successfully added to the cart.
 * Called from CartContext after state update confirms addition.
 * @param {number} qty - quantity added in this action
 * @param {number} unitPrice - price per unit (GBP)
 */
export function trackAddToCart(qty, unitPrice) {
  if (typeof window.fbq !== 'function') return;
  window.fbq('track', 'AddToCart', {
    content_ids: ['reload-male-performance-formula'],
    content_name: 'RELOAD Male Performance Formula',
    content_type: 'product',
    value: parseFloat((qty * unitPrice).toFixed(2)),
    currency: 'GBP',
    num_items: qty,
  });
}

/**
 * Fires when the customer clicks "Proceed to Checkout".
 * @param {number} totalQty - total items in cart
 * @param {number} subtotal - cart subtotal (GBP)
 */
export function trackInitiateCheckout(totalQty, subtotal) {
  if (typeof window.fbq !== 'function') return;
  window.fbq('track', 'InitiateCheckout', {
    content_ids: ['reload-male-performance-formula'],
    content_type: 'product',
    num_items: totalQty,
    value: parseFloat(subtotal.toFixed(2)),
    currency: 'GBP',
  });
}

// ─── UTM preservation ────────────────────────────────────────────────────────

/**
 * Captures UTM parameters from the current URL into sessionStorage.
 * Call once on app init. Re-captures if new UTMs arrive (new paid click).
 */
export function captureUTMs() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const utms = {};
    UTM_KEYS.forEach((key) => {
      const val = params.get(key);
      if (val) utms[key] = val;
    });
    if (Object.keys(utms).length > 0) {
      sessionStorage.setItem(UTM_SESSION_KEY, JSON.stringify(utms));
    }
  } catch {
    // sessionStorage unavailable — skip silently
  }
}

/**
 * Appends stored UTM parameters to a URL (e.g. Shopify checkout URL).
 * Uses '?' or '&' correctly based on whether a query string already exists.
 */
export function appendUTMsToUrl(url) {
  try {
    const stored = sessionStorage.getItem(UTM_SESSION_KEY);
    if (!stored) return url;
    const utms = JSON.parse(stored);
    const pairs = Object.entries(utms)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    if (!pairs) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${pairs}`;
  } catch {
    return url;
  }
}
